import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useMessages } from '../../context/MessagesContext';
import { MessageBubble } from './MessageBubble';

export function MessageList({ messages, selectedContact }) {
  const { user } = useAuth();
  const { messagesEndRef, messagesContainerRef, handleScroll, scrollToBottom, shouldAutoScroll, markAsRead } = useMessages();
  const observerRef = useRef(null);

  useEffect(() => {
    if (shouldAutoScroll) {
      scrollToBottom('smooth');
    }
  }, [messages, scrollToBottom, shouldAutoScroll]);

  // Setup intersection observer to mark messages as read when visible
  useEffect(() => {
    if (!selectedContact) return;

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const messageId = entry.target.dataset.messageId;
          if (messageId) {
            const message = messages.find(m => m.id === messageId);
            if (message && !message.readAt && !message.isOwn) {
              markAsRead(messageId);
            }
          }
        }
      });
    }, { threshold: 0.5 });

    const messageElements = document.querySelectorAll('[data-message-id]');
    messageElements.forEach(el => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [messages, selectedContact, markAsRead]);

  const filteredMessages = messages.filter(msg => {
    const isRelevant = (
      (msg.senderUuid === user.uuid && msg.recipientUuid === selectedContact?.contactUuid) ||
      (msg.senderUuid === selectedContact?.contactUuid && msg.recipientUuid === user.uuid)
    );
    
    const isNotExpired = !msg.expiresAt || new Date(msg.expiresAt) > new Date();
    
    return isRelevant && isNotExpired;
  }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  if (!selectedContact) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        Select a contact to start chatting
      </div>
    );
  }

  if (filteredMessages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        <div className="text-center">
          <div className="bg-gray-100 p-8 rounded-full mb-4 inline-block">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <p className="text-lg font-medium">No messages yet</p>
          <p className="text-sm">Send your first message to {selectedContact.alias || 'this contact'}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="flex-1 overflow-y-auto p-4 space-y-4" 
      ref={messagesContainerRef}
      onScroll={handleScroll}
    >
      {filteredMessages.map((msg, index, filteredMsgs) => {
        const showDate = index === 0 || 
          new Date(msg.timestamp).toDateString() !== new Date(filteredMsgs[index - 1]?.timestamp).toDateString();
        
        return (
          <React.Fragment key={msg.id}>
            {showDate && (
              <div className="flex justify-center my-4">
                <span className="bg-gray-300 text-gray-700 text-xs px-3 py-1 rounded-full">
                  {new Date(msg.timestamp).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
            )}
            <div data-message-id={msg.id}>
              <MessageBubble 
                message={msg} 
                isOwn={msg.senderUuid === user.uuid}
                contact={selectedContact}
              />
            </div>
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}